import { useCallback, useState } from "react";

import { DataGridPro, GridCellParams, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro";

import { ContactsAnalyticsService } from "@analytics/index";

import { ContactType } from "@interfaces/contact.type";

import TableOverlay from "@pages/Companies/components/TableOverlay";

import { useTableStore } from "@store/filters/filters.store";
import { PopupActions } from "@store/popupStore";

import { getRowClassName, tableStyles } from "../Table/tableStyles";

interface IContactTableTypedProperties {
  columns: GridColDef[];
  contacts: ContactType[];
  isLoading?: boolean;
  paginationMode: "client" | "server";
  rowMenuItems?: { func: (contactId: string) => void; label: string }[];
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  shouldUseTabHeight?: boolean;
  total?: number;
}

/**
 * Provides a data grid table of contacts, with pagination and sorting capabilities. This component is used in the Contact Select workflow.
 * @param columns - Array of column definitions for the data grid table.
 * @param handleRowSelectionChange - Callback function to handle row selection changes.
 * @param isLoading - Whether the table is currently loading data.
 * @param paginationMode - Whether the table is currently viewing technology contacts.
 * @param selectedIds - Array of selected contact ids.
 * @param setSelectedIds - Callback function to set the selected contact ids.
 * @param total - Total number of contacts.
 */
export default function ContactsTable({
  columns,
  contacts,
  isLoading,
  paginationMode,
  selectedIds,
  setSelectedIds,
  shouldUseTabHeight = false,
  total,
}: IContactTableTypedProperties) {
  const [sortCount, setSortCount] = useState(0);

  const { pagination, setTablePagination } = useTableStore("contact");

  const maxSelection = 200;

  const handleRowSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      const startIndex = (pagination?.page ?? 0) * pagination.pageSize;
      const endIndex = startIndex + maxSelection;
      if (newSelection.length > maxSelection) {
        PopupActions.openAlert({
          duration: 4000,
          message: "You can only select up to 200 contacts at a time.",
          severity: "info",
        });
        setSelectedIds(newSelection.slice(startIndex, endIndex) as string[]);
      } else {
        setSelectedIds(newSelection as string[]);
      }
    },
    [pagination, setSelectedIds],
  );

  const handleCellClick = useCallback(
    (cellParameters: GridCellParams) => {
      if (["name"].includes(cellParameters.field)) {
        setSelectedIds(
          selectedIds.includes(cellParameters.id as string)
            ? selectedIds.filter((id) => id !== cellParameters.id)
            : [...selectedIds, cellParameters.id as string],
        );
      }
    },
    [selectedIds, setSelectedIds],
  );

  return (
    <DataGridPro
      checkboxSelection
      checkboxSelectionVisibleOnly={true}
      className={shouldUseTabHeight ? "tab-height" : ""}
      columns={columns}
      disableColumnMenu={true}
      disableRowSelectionOnClick
      getRowClassName={getRowClassName}
      initialState={{
        pagination: { paginationModel: { page: pagination.page, pageSize: pagination.pageSize } },
      }}
      loading={isLoading}
      onCellClick={handleCellClick}
      onPaginationModelChange={(model) => {
        setTablePagination(model);
      }}
      onRowSelectionModelChange={handleRowSelectionChange}
      onSortModelChange={(sortModel) => {
        const newSortCount = sortCount + 1;
        if (sortModel[0] && sortModel[0].field && sortModel[0].sort) {
          setSortCount(newSortCount);
        }
      }}
      pageSizeOptions={[50, 100, 200]}
      pagination
      paginationMode={paginationMode}
      paginationModel={pagination}
      rowCount={total}
      rows={contacts}
      rowSelectionModel={selectedIds}
      slots={{
        loadingOverlay: () => <TableOverlay subtitle="This can take up to one minute." title="Getting Contacts" />,
      }}
      sx={{ ...tableStyles.root }}
    />
  );
}
